/**
 * 606 Meta5 AI Trading - MetaApi Profiles Panel (مفاتيح MetaApi)
 *
 * Lets the admin add multiple MetaApi tokens (e.g. one for Demo, one for Live)
 * and switch between them with one click. Switching disconnects the existing
 * MT5 broker connection automatically.
 */
import React, { useState, useEffect, useCallback } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { toast } from 'sonner';
import {
  KeyRound, Plus, Loader2, CheckCircle2, Trash2, Power,
  Eye, EyeOff, AlertTriangle, ShieldAlert, Edit3, X, Save,
} from 'lucide-react';
import {
  fetchMetaApiProfiles, createMetaApiProfile, updateMetaApiProfile,
  deleteMetaApiProfile, activateMetaApiProfile,
} from '../../lib/trading-api';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter,
} from '../../components/ui/dialog';

const MODE_META = {
  demo:  { ar: 'تجريبي',  en: 'Demo',  color: '#3b82f6' },
  live:  { ar: 'حقيقي',   en: 'Live',  color: '#ef4444' },
  mixed: { ar: 'مختلط',   en: 'Mixed', color: '#a855f7' },
};

const MetaApiProfilesPanel = ({ language = 'ar', onActiveChanged }) => {
  const isAr = language === 'ar';
  const [loading, setLoading] = useState(true);
  const [profiles, setProfiles] = useState([]);
  const [activeId, setActiveId] = useState(null);
  const [envAvailable, setEnvAvailable] = useState(false);
  const [busyId, setBusyId] = useState(null);
  const [showAdd, setShowAdd] = useState(false);
  const [showTokenId, setShowTokenId] = useState(null);
  const [editingProfile, setEditingProfile] = useState(null);
  const [confirmDelete, setConfirmDelete] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const data = await fetchMetaApiProfiles();
      setProfiles(data.profiles || []);
      setActiveId(data.active_id || null);
      setEnvAvailable(!!data.env_fallback_available);
    } catch (e) {
      toast.error(isAr ? 'فشل تحميل المفاتيح' : 'Failed to load profiles');
    } finally {
      setLoading(false);
    }
  }, [isAr]);

  useEffect(() => { load(); }, [load]);

  const handleActivate = async (p) => {
    setBusyId(p.id);
    try {
      const res = await activateMetaApiProfile(p.id);
      if (res.success) {
        toast.success(
          isAr
            ? `تم تفعيل "${p.name}"${res.disconnected_existing ? ' - تم قطع الاتصال السابق' : ''}`
            : `Activated "${p.name}"${res.disconnected_existing ? ' - previous connection dropped' : ''}`,
        );
        await load();
        if (onActiveChanged) onActiveChanged();
      }
    } catch (e) {
      toast.error(e?.response?.data?.detail || (isAr ? 'فشل التفعيل' : 'Failed to activate'));
    } finally {
      setBusyId(null);
    }
  };

  const handleDelete = async (p) => {
    setConfirmDelete(null);
    setBusyId(p.id);
    try {
      await deleteMetaApiProfile(p.id);
      toast.success(isAr ? `تم حذف "${p.name}"` : `Deleted "${p.name}"`);
      await load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || (isAr ? 'فشل الحذف' : 'Failed to delete'));
    } finally {
      setBusyId(null);
    }
  };

  return (
    <motion.div
      initial={{ opacity: 0, y: 10 }}
      animate={{ opacity: 1, y: 0 }}
      className="bg-white/[0.02] backdrop-blur-xl rounded-xl border border-white/10 p-5"
      data-testid="metaapi-profiles-panel"
    >
      {/* Header */}
      <div className="flex items-center justify-between mb-4 flex-wrap gap-2">
        <div className="flex items-center gap-2">
          <KeyRound className="h-5 w-5 text-[#d4af37]" />
          <h3 className="text-base font-semibold text-white">
            {isAr ? 'مفاتيح MetaApi' : 'MetaApi Profiles'}
          </h3>
          {profiles.length > 0 && (
            <span className="px-2 py-0.5 bg-[#d4af37]/10 text-[#d4af37] text-xs rounded-full">
              {profiles.length}
            </span>
          )}
        </div>
        <Button
          onClick={() => setShowAdd(true)}
          size="sm"
          className="bg-[#d4af37] hover:bg-[#c19b2e] text-black font-bold gap-1.5"
          data-testid="metaapi-add-btn"
        >
          <Plus className="h-3.5 w-3.5" />
          {isAr ? 'إضافة مفتاح' : 'Add Profile'}
        </Button>
      </div>

      <p className="text-xs text-[#A1A1AA] mb-4 leading-relaxed">
        {isAr
          ? 'أضف عدة مفاتيح MetaApi (مثل: مفتاح للديمو ومفتاح للحقيقي) وبدّل بينها بضغطة زر. سيتم قطع الاتصال الحالي بـ MT5 تلقائياً عند التبديل.'
          : 'Add multiple MetaApi tokens (e.g. one for Demo, one for Live) and switch with one click. The active MT5 connection is dropped automatically on switch.'}
      </p>

      {loading ? (
        <div className="flex items-center justify-center py-8">
          <Loader2 className="h-6 w-6 animate-spin text-[#d4af37]" />
        </div>
      ) : profiles.length === 0 ? (
        <EmptyState isAr={isAr} envAvailable={envAvailable} onAdd={() => setShowAdd(true)} />
      ) : (
        <div className="space-y-2" data-testid="metaapi-profiles-list">
          {profiles.map((p) => (
            <ProfileRow
              key={p.id}
              profile={p}
              isActive={activeId === p.id}
              isAr={isAr}
              busy={busyId === p.id}
              showToken={showTokenId === p.id}
              onToggleToken={() => setShowTokenId(showTokenId === p.id ? null : p.id)}
              onActivate={() => handleActivate(p)}
              onEdit={() => setEditingProfile(p)}
              onDelete={() => setConfirmDelete(p)}
            />
          ))}
        </div>
      )}

      {/* Add dialog */}
      <ProfileDialog
        open={showAdd}
        onClose={() => setShowAdd(false)}
        onSaved={() => { setShowAdd(false); load(); }}
        isAr={isAr}
        mode="create"
      />

      {/* Edit dialog */}
      <ProfileDialog
        open={!!editingProfile}
        onClose={() => setEditingProfile(null)}
        onSaved={() => { setEditingProfile(null); load(); }}
        isAr={isAr}
        mode="edit"
        profile={editingProfile}
      />

      {/* Delete confirm */}
      <Dialog open={!!confirmDelete} onOpenChange={(o) => { if (!o) setConfirmDelete(null); }}>
        <DialogContent className="bg-[#0a0a0a] border-red-500/30 text-white max-w-md" data-testid="metaapi-delete-dialog">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 text-red-400">
              <ShieldAlert className="h-5 w-5" />
              {isAr ? 'حذف المفتاح' : 'Delete Profile'}
            </DialogTitle>
            <DialogDescription className="text-[#A1A1AA] mt-2">
              {confirmDelete && (
                <>
                  {isAr
                    ? `هل أنت متأكد من حذف المفتاح "${confirmDelete.name}"؟ هذا الإجراء لا يمكن التراجع عنه. المفتاح نفسه على MetaApi.cloud لن يُحذف.`
                    : `Delete "${confirmDelete.name}" profile? This cannot be undone. The token on MetaApi.cloud itself will NOT be deleted.`}
                </>
              )}
            </DialogDescription>
          </DialogHeader>
          <DialogFooter className="gap-2">
            <Button
              variant="outline"
              onClick={() => setConfirmDelete(null)}
              className="border-white/10 text-white hover:bg-white/5"
            >
              {isAr ? 'إلغاء' : 'Cancel'}
            </Button>
            <Button
              onClick={() => handleDelete(confirmDelete)}
              className="bg-red-500 hover:bg-red-600 text-white gap-1.5"
              data-testid="metaapi-delete-confirm"
            >
              <Trash2 className="h-4 w-4" />
              {isAr ? 'حذف' : 'Delete'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </motion.div>
  );
};

// ============================================================
function EmptyState({ isAr, envAvailable, onAdd }) {
  return (
    <div className="text-center py-8 bg-white/[0.02] border border-dashed border-white/10 rounded-xl">
      <KeyRound className="h-10 w-10 text-[#71717a] mx-auto mb-2" />
      <p className="text-sm text-[#A1A1AA] mb-1">
        {isAr ? 'لا توجد مفاتيح MetaApi مضافة بعد' : 'No MetaApi profiles yet'}
      </p>
      {envAvailable && (
        <p className="text-xs text-[#71717a] mb-3">
          {isAr
            ? '⚠️ يوجد توكن في إعدادات النظام (.env) سيُستخدم كاحتياطي حتى تضيف أول مفتاح.'
            : '⚠️ A fallback token from the system env is currently in use until you add your first profile.'}
        </p>
      )}
      <Button onClick={onAdd} size="sm" className="bg-[#d4af37] hover:bg-[#c19b2e] text-black gap-1.5 mt-2">
        <Plus className="h-3.5 w-3.5" />
        {isAr ? 'أضف أول مفتاح' : 'Add your first profile'}
      </Button>
    </div>
  );
}

// ============================================================
function ProfileRow({ profile, isActive, isAr, busy, showToken, onToggleToken, onActivate, onEdit, onDelete }) {
  const mode = MODE_META[profile.mode_hint] || MODE_META.demo;
  return (
    <div
      data-testid={`metaapi-profile-${profile.id}`}
      className={`p-3 rounded-xl border transition-all flex items-center justify-between gap-3 ${
        isActive
          ? 'bg-emerald-500/5 border-emerald-500/40'
          : 'bg-white/[0.02] border-white/10 hover:border-white/20'
      }`}
    >
      <div className="flex-1 min-w-0">
        <div className="flex items-center gap-2 flex-wrap">
          <span className="font-semibold text-white">{profile.name}</span>
          <span
            className="px-1.5 py-0.5 text-[9px] rounded-full font-bold border"
            style={{ color: mode.color, backgroundColor: `${mode.color}15`, borderColor: `${mode.color}40` }}
          >
            {isAr ? mode.ar : mode.en}
          </span>
          {isActive && (
            <span className="inline-flex items-center gap-1 px-2 py-0.5 text-[9px] rounded-full bg-emerald-500/20 text-emerald-400 font-bold">
              <CheckCircle2 className="h-2.5 w-2.5" />
              {isAr ? 'نشط' : 'ACTIVE'}
            </span>
          )}
        </div>
        <div className="flex items-center gap-2 mt-1 text-xs text-[#71717a]">
          <span className="font-mono">
            {showToken ? `(${profile.token_length} حرف)` : profile.token_preview}
          </span>
          <button
            onClick={onToggleToken}
            className="text-[#d4af37] hover:text-[#c19b2e] transition-colors"
            type="button"
            data-testid={`metaapi-profile-toggle-token-${profile.id}`}
            title={isAr ? 'إظهار/إخفاء' : 'Show/Hide'}
          >
            {showToken ? <EyeOff className="h-3 w-3" /> : <Eye className="h-3 w-3" />}
          </button>
          {profile.last_used_at && (
            <span className="text-[10px] hidden md:inline">
              · {isAr ? 'آخر استخدام:' : 'last used:'} {new Date(profile.last_used_at).toLocaleString(isAr ? 'ar' : 'en')}
            </span>
          )}
        </div>
      </div>

      <div className="flex items-center gap-1.5 shrink-0">
        {!isActive && (
          <Button
            onClick={onActivate}
            disabled={busy}
            size="sm"
            className="bg-emerald-500 hover:bg-emerald-600 text-white gap-1 h-8 text-xs"
            data-testid={`metaapi-profile-activate-${profile.id}`}
          >
            {busy ? <Loader2 className="h-3 w-3 animate-spin" /> : <Power className="h-3 w-3" />}
            {isAr ? 'تفعيل' : 'Activate'}
          </Button>
        )}
        <Button
          onClick={onEdit}
          size="icon"
          variant="ghost"
          className="h-8 w-8 text-[#A1A1AA] hover:bg-white/5"
          data-testid={`metaapi-profile-edit-${profile.id}`}
          title={isAr ? 'تعديل' : 'Edit'}
        >
          <Edit3 className="h-3.5 w-3.5" />
        </Button>
        <Button
          onClick={onDelete}
          size="icon"
          variant="ghost"
          className="h-8 w-8 text-red-400 hover:bg-red-500/10"
          data-testid={`metaapi-profile-delete-${profile.id}`}
          title={isAr ? 'حذف' : 'Delete'}
        >
          <Trash2 className="h-3.5 w-3.5" />
        </Button>
      </div>
    </div>
  );
}

// ============================================================
function ProfileDialog({ open, onClose, onSaved, isAr, mode = 'create', profile = null }) {
  const isEdit = mode === 'edit';
  const [name, setName] = useState('');
  const [token, setToken] = useState('');
  const [modeHint, setModeHint] = useState('demo');
  const [showToken, setShowToken] = useState(false);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (open) {
      setName(profile?.name || '');
      setToken(''); // never prefill the token (it's redacted server-side)
      setModeHint(profile?.mode_hint || 'demo');
      setShowToken(false);
    }
  }, [open, profile]);

  const handleSave = async () => {
    const trimmedName = name.trim();
    const trimmedToken = token.trim();
    if (!trimmedName) {
      toast.error(isAr ? 'أدخل اسم المفتاح' : 'Enter a profile name');
      return;
    }
    if (!isEdit && trimmedToken.length < 20) {
      toast.error(isAr ? 'التوكن قصير جداً (يجب 20 حرف على الأقل)' : 'Token is too short (min 20 chars)');
      return;
    }
    setSaving(true);
    try {
      if (isEdit) {
        const payload = { name: trimmedName, mode_hint: modeHint };
        if (trimmedToken.length >= 20) payload.token = trimmedToken;
        await updateMetaApiProfile(profile.id, payload);
        toast.success(isAr ? 'تم التحديث' : 'Updated');
      } else {
        await createMetaApiProfile({ name: trimmedName, token: trimmedToken, mode_hint: modeHint });
        toast.success(isAr ? 'تم إضافة المفتاح' : 'Profile added');
      }
      onSaved();
    } catch (e) {
      toast.error(e?.response?.data?.detail || (isAr ? 'فشل الحفظ' : 'Failed to save'));
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={(o) => { if (!o) onClose(); }}>
      <DialogContent className="bg-[#0a0a0a] border-white/10 text-white max-w-md" data-testid="metaapi-profile-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <KeyRound className="h-5 w-5 text-[#d4af37]" />
            {isEdit
              ? (isAr ? 'تعديل المفتاح' : 'Edit Profile')
              : (isAr ? 'إضافة مفتاح MetaApi' : 'Add MetaApi Profile')}
          </DialogTitle>
          <DialogDescription className="text-[#A1A1AA] text-xs mt-1">
            {isAr
              ? 'احصل على التوكن من MetaApi.cloud → API Access → Generate Token.'
              : 'Get your token from MetaApi.cloud → API Access → Generate Token.'}
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-3 py-2">
          <div>
            <label className="text-xs text-[#A1A1AA] mb-1 block">
              {isAr ? 'اسم المفتاح' : 'Profile Name'} *
            </label>
            <Input
              value={name}
              onChange={(e) => setName(e.target.value)}
              placeholder={isAr ? 'مثال: Demo Profile' : 'e.g. Demo Profile'}
              className="bg-white/5 border-white/10 text-white"
              data-testid="metaapi-profile-name-input"
            />
          </div>

          <div>
            <label className="text-xs text-[#A1A1AA] mb-1 block">
              {isAr ? 'التوكن (Token)' : 'Token'} {!isEdit && '*'}
              {isEdit && <span className="text-[10px] text-[#71717a] ms-2">{isAr ? '(اتركه فاضي للإبقاء على القديم)' : '(leave empty to keep current)'}</span>}
            </label>
            <div className="relative">
              <textarea
                value={token}
                onChange={(e) => setToken(e.target.value)}
                placeholder={isEdit ? '' : 'eyJhbGciOiJSUzUxMiIs…'}
                className="w-full bg-white/5 border border-white/10 rounded-md px-3 py-2 text-white text-xs font-mono resize-none focus:outline-none focus:ring-1 focus:ring-[#d4af37]"
                rows={4}
                style={{ fontFamily: showToken ? 'monospace' : 'text-security-disc, monospace', WebkitTextSecurity: showToken ? 'none' : 'disc' }}
                data-testid="metaapi-profile-token-input"
              />
              <button
                type="button"
                onClick={() => setShowToken(!showToken)}
                className="absolute top-2 end-2 text-[#71717a] hover:text-[#d4af37]"
                title={isAr ? 'إظهار/إخفاء' : 'Show/Hide'}
              >
                {showToken ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
              </button>
            </div>
          </div>

          <div>
            <label className="text-xs text-[#A1A1AA] mb-1 block">
              {isAr ? 'النوع' : 'Type'}
            </label>
            <div className="grid grid-cols-3 gap-2">
              {['demo', 'live', 'mixed'].map((m) => {
                const meta = MODE_META[m];
                const active = modeHint === m;
                return (
                  <button
                    key={m}
                    type="button"
                    onClick={() => setModeHint(m)}
                    className="px-3 py-2 rounded-md border text-xs font-bold transition-all"
                    style={{
                      backgroundColor: active ? `${meta.color}25` : 'transparent',
                      borderColor: active ? meta.color : 'rgba(255,255,255,0.10)',
                      color: active ? meta.color : '#A1A1AA',
                    }}
                    data-testid={`metaapi-mode-${m}`}
                  >
                    {isAr ? meta.ar : meta.en}
                  </button>
                );
              })}
            </div>
            <p className="text-[10px] text-[#71717a] mt-1">
              {isAr
                ? 'النوع للعرض فقط — التوكن نفسه يقرر أي حسابات (Demo/Live) يمكنه الوصول إليها.'
                : 'Type is informational — the token itself controls which accounts (Demo/Live) it can access.'}
            </p>
          </div>

          <div className="bg-amber-500/5 border border-amber-500/20 rounded-md p-2 flex gap-2">
            <AlertTriangle className="h-4 w-4 text-amber-400 shrink-0 mt-0.5" />
            <p className="text-[10px] text-amber-200/80 leading-relaxed">
              {isAr
                ? 'لن يتم عرض التوكن بشكل كامل بعد الحفظ. احتفظ بنسخة منه في مكان آمن.'
                : 'The token will be hidden after save. Keep a copy in a secure place.'}
            </p>
          </div>
        </div>

        <DialogFooter className="gap-2">
          <Button variant="outline" onClick={onClose} className="border-white/10 text-white hover:bg-white/5">
            {isAr ? 'إلغاء' : 'Cancel'}
          </Button>
          <Button
            onClick={handleSave}
            disabled={saving}
            className="bg-[#d4af37] hover:bg-[#c19b2e] text-black font-bold gap-1.5"
            data-testid="metaapi-profile-save"
          >
            {saving ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Save className="h-3.5 w-3.5" />}
            {isEdit ? (isAr ? 'حفظ التغييرات' : 'Save Changes') : (isAr ? 'إضافة' : 'Add')}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

export default MetaApiProfilesPanel;
